import React from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { Popover, message } from 'antd';
import { CheckCircleFilled } from '@ant-design/icons';
import { getCalendarEventColor } from '../../utilities/getScheduleTypeStyles';
import moment from 'moment';
import axios from 'axios';
import '../../schedule/coach-calendar/CoachCalendar.scss';

const EventPopover = ({ eventInfo, onRefresh }) => {
  const [visible, setVisible] = React.useState(false);
  const [confirmLoading, setConfirmLoading] = React.useState(false);
  const history = useHistory();
  const schedule = useSelector((state) => state.schedule);

  const endTime = eventInfo.event.end;
  const isPast = moment(endTime).isBefore(moment());
  const isComplete = eventInfo.event.extendedProps?.is_complete === true;
  const isConfirmed =
    eventInfo.event.extendedProps?.schedule_confirmed === true;
  const eventDate = moment(eventInfo.event.start).format('YYYY-MM-DD');
  const isToday = eventDate === moment().format('YYYY-MM-DD');

  const handleVisibleChange = (newVisible) => {
    setVisible(newVisible);
  };

  const handleViewSchedule = () => {
    const { type_id } = eventInfo.event.extendedProps;
    setVisible(false);
    history.push(`/coach-dashboard/schedule?typeId=${type_id}`);
  };

  const handleConfirmSchedule = async () => {
    setConfirmLoading(true);
    try {
      await axios.post(
        `${
          process.env.REACT_APP_SERVER_URL || ''
        }/schedules/coach_schedule_confirmation/`,
        { confirmed: true, date: eventDate }
      );
      message.success('Schedule confirmed successfully!');
      setVisible(false);
      if (onRefresh) onRefresh();
    } catch (error) {
      message.error('Failed to confirm schedule');
    } finally {
      setConfirmLoading(false);
    }
  };

  const getEventStyle = () => {
    const baseStyle = getCalendarEventColor(
      schedule,
      eventInfo.event.extendedProps?.type_id
    );

    if (isPast || isComplete) {
      return {
        ...baseStyle,
        opacity: 0.5,
        filter: 'brightness(0.85)',
      };
    }
    return baseStyle;
  };

  const popoverContent = (eventInfo) => {
    return (
      <div key={eventInfo.event.extendedProps.id}>
        <div className="flex">
          <h3>{eventInfo.event.extendedProps.program} -</h3>
          <h3>&nbsp;{eventInfo.event.title}</h3>
        </div>
        <h4>{eventInfo.timeText}</h4>
        <div className="flex">
          <h4>Pickup Location:</h4>
          <p>&nbsp;{eventInfo.event.extendedProps?.start_location} </p>
        </div>
        <div className="flex">
          <h4>Destination:</h4>
          <p>&nbsp;{eventInfo.event.extendedProps?.end_location} </p>
        </div>
        <div className="flex">
          <h4>Transport Type:</h4>
          <p>&nbsp;{eventInfo.event.extendedProps?.transport_type} </p>
        </div>
        <div className="non-flex">
          <h4>Participants:</h4>
          {eventInfo.event.extendedProps.participants.map((p, index) => (
            <p key={index}>{p}</p>
          ))}
        </div>
        <div className="non-flex">
          <h4>Participant status:</h4>
          {eventInfo.event.extendedProps?.participant_status.map((p, index) => (
            <p key={index}>{p}</p>
          ))}
        </div>
        {eventInfo.event.extendedProps.skills.length > 0 ? (
          <div className="non-flex">
            <h4>Skills:</h4>
            {eventInfo.event.extendedProps?.skills.map((skill, index) => (
              <div key={index}>
                <p>{skill}</p>
              </div>
            ))}
          </div>
        ) : (
          <h4>No skills provided</h4>
        )}
        {eventInfo.event.extendedProps.addresses ? (
          <div className="non-flex">
            <h4>Participant Addresses:</h4>
            {eventInfo.event.extendedProps?.addresses.map((p, index) => (
              <p key={index}>{p}</p>
            ))}
          </div>
        ) : (
          <p>No participant address provided</p>
        )}
        {eventInfo.event.extendedProps.note && (
          <div className="non-flex">
            <h4>Note:</h4>
            <p>{eventInfo.event.extendedProps.note}</p>
          </div>
        )}
        {eventInfo.event.extendedProps.participant_notes.length > 0 && (
          <div className="non-flex">
            <h4>Participant Notes:</h4>
            {eventInfo.event.extendedProps.participant_notes.map(
              (note, index) => (
                <p key={index}>{note}</p>
              )
            )}
          </div>
        )}
        <div
          style={{
            display: 'flex',
            gap: '8px',
            marginTop: '8px',
            flexWrap: 'wrap',
          }}
        >
          <button
            className="close-btn"
            style={{
              ...getCalendarEventColor(
                schedule,
                eventInfo.event.extendedProps?.type_id
              ),
              cursor: 'pointer',
            }}
            onClick={() => setVisible(false)}
          >
            Close
          </button>
          {isToday && (
            <>
              <button
                className="close-btn"
                style={{
                  ...getCalendarEventColor(
                    schedule,
                    eventInfo.event.extendedProps?.type_id
                  ),
                  cursor: 'pointer',
                }}
                onClick={handleViewSchedule}
              >
                View Schedule
              </button>
              <button
                className="close-btn"
                style={{
                  ...getCalendarEventColor(
                    schedule,
                    eventInfo.event.extendedProps?.type_id
                  ),
                  cursor: isConfirmed ? 'default' : 'pointer',
                  backgroundColor: isConfirmed ? '#52c41a' : undefined,
                  borderColor: isConfirmed ? '#52c41a' : undefined,
                  color: isConfirmed ? '#fff' : undefined,
                  opacity: confirmLoading ? 0.7 : 1,
                }}
                onClick={!isConfirmed ? handleConfirmSchedule : undefined}
                disabled={isConfirmed || confirmLoading}
              >
                {confirmLoading
                  ? 'Confirming...'
                  : isConfirmed
                  ? 'Confirmed ✓'
                  : 'Confirm Schedule'}
              </button>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <Popover
      content={popoverContent(eventInfo)}
      placement="right"
      overlayClassName="popover-info"
      title={eventInfo.event?.title}
      trigger="click"
      visible={visible}
      onVisibleChange={handleVisibleChange}
    >
      <div
        onClick={() => setVisible(true)}
        style={getEventStyle()}
        className="day-event-wrapper"
      >
        <div className="event-header">
          <h4>{eventInfo.event.extendedProps.program}</h4>
          <h3 className="event-header">
            {eventInfo.event.title}
            {(isComplete || isConfirmed) && (
              <CheckCircleFilled style={{ marginLeft: 8, color: '#52c41a' }} />
            )}
          </h3>
          <p className="time-text">{eventInfo.timeText}</p>
        </div>
        <div className="participants-header">
          <p>PARTICIPANTS</p>
          {eventInfo.event.extendedProps.participants && (
            <div>
              {eventInfo.event.extendedProps.participants.map((p, index) => (
                <h5
                  className={
                    eventInfo.event.extendedProps.participant_status[index] ===
                    `${p}: scheduled`
                      ? 'participants'
                      : 'participant-out'
                  }
                  key={index}
                >
                  {p}
                </h5>
              ))}
            </div>
          )}
        </div>
        <div className="location">
          <p>PICKUP</p>
          <h5>
            {eventInfo.event.extendedProps?.start_location
              ? eventInfo.event.extendedProps?.start_location
              : 'Unknown'}
          </h5>
        </div>
        <div className="location">
          <p>DESTINATION</p>
          <h5>
            {eventInfo.event.extendedProps?.end_location
              ? eventInfo.event.extendedProps?.end_location
              : 'Unknown'}
          </h5>
        </div>
      </div>
    </Popover>
  );
};

export default EventPopover;
